import React, { useState, useEffect, useMemo } from "react";

import { Download } from "lucide-react";

import PageContainer from "../../components/common/PageContainer";
import useIsMobile from "../../hooks/useIsMobile";

import LeadsFilters from "../../components/leads/LeadsFilters";
import LeadsTable from "../../components/leads/LeadsTable";
import useLeadsFilter from "../../hooks/useLeadsFilter";
import { useColumns } from "../../components/leads/leadsColumns";

import { toast } from "react-toastify";

import {
  useLeadsQuery,
  useExportLeads,
  useResendLead,
} from "../../queries/leads/useLeadsQuery";

import Button from "../../components/ui/Button";
import Pagination from "../../components/common/pagination/Pagination";
import { normalizeLeads } from "../../utils/normalizeLeads";


export default function Leads() {
  const isMobile = useIsMobile();

  const { data: leadsList, isLoading: isLeadsLoading } = useLeadsQuery();
  const { mutate: exportLeads } = useExportLeads();
  const { mutate: resendLead } = useResendLead();


  const leads = normalizeLeads(leadsList?.data ?? []);
  /* ---------------- FILTER STATES ---------------- */
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterProperties, setFilterProperties] = useState("All");
  const [filterAgent, setFilterAgent] = useState("All");
  const [filterSource, setFilterSource] = useState("All");
  const [sortDate, setSortDate] = useState("Latest");
  /* ---------------- PAGINATION STATES ---------------- */
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  /* ---------------- DATA ---------------- */

  const agents = useMemo(
    () => ["All", ...new Set(leads.map((l) => l.agent_name))],
    [leads],
  );
  const properties = useMemo(
    () => ["All", ...new Set(leads.map((l) => l.property_name))],
    [leads],
  );

  /* ---------------- FILTER ---------------- */

  const filteredLeads = useLeadsFilter(
    leads,
    filterCategory,
    filterProperties,
    filterAgent,
    filterSource,
    sortDate,
  );

  /* ---------------- PAGINATION LOGIC ---------------- */

  const totalPages = Math.ceil(filteredLeads.length / pageSize);

  const paginatedLeads = useMemo(
    () =>
      filteredLeads.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredLeads, currentPage],
  );

  /* Reset page when filters change */
  useEffect(() => {
    setCurrentPage(1);
  }, [filterCategory, filterProperties, filterAgent, filterSource, sortDate]);

  /* ---------------- ACTION ---------------- */

  const handleResend = (lead) => {
    resendLead(lead.id, {
      onSuccess: () => {
        toast.success("Lead resent successfully");
      },
      onError: (error) => {
        console.error("Failed to resend lead", error);
        toast.error("Failed to resend lead");
      },
    });
  };

  const columns = useColumns();

  const handleExport = () => {
    exportLeads(undefined, {
      onSuccess: async (response) => {
        const blob = response.data;

        if (!(blob instanceof Blob)) {
          toast.error("Export failed: invalid response");
          return;
        }

        if (blob.type && blob.type.includes("application/json")) {
          try {
            const text = await blob.text();
            const body = text ? JSON.parse(text) : {};
            toast.error(body.message || body.detail || "Export failed");
          } catch {
            toast.error("Export failed");
          }
          return;
        }

        const cd =
          response.headers["content-disposition"] ||
          response.headers["Content-Disposition"];
        let fileName = "leads.xlsx";

        if (cd) {
          let m = cd.match(/filename\*=UTF-8''([^;\n]+)/i);
          if (m?.[1]) {
            fileName = decodeURIComponent(m[1].replace(/"/g, "").trim());
          } else {
            m = cd.match(/filename="?([^";\n]+)"?/i);
            if (m?.[1]) fileName = m[1].trim();
          }
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("Export success");
      },
      onError: (error) => {
        console.error("Export failed", error);
        const msg =
          typeof error === "string"
            ? error
            : error?.message || error?.detail || "Export failed";
        toast.error(msg);
      },
    });
  };

  let buttonText = "Export XLSX";
  if (isMobile) {
    buttonText = "";
  }
  /* ---------------- UI ---------------- */

  // if (isLeadsLoading) {
  //   return <Loader />;
  // }

  return (
    <PageContainer>
      {/* Header */}
      <div className="d-flex align-center justify-between mb-4">
        <h2 className="text-primary mb-3">Lead Management</h2>
        <div>
          <Button onClick={handleExport} className="d-flex align-center gap-1">
            <Download />
            {buttonText}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <LeadsFilters
        filterAgent={filterAgent}
        setFilterAgent={setFilterAgent}
        filterProperties={filterProperties}
        setFilterProperties={setFilterProperties}
        filterCategory={filterCategory}
        setFilterCategory={setFilterCategory}
        filterSource={filterSource}
        setFilterSource={setFilterSource}
        sortDate={sortDate}
        setSortDate={setSortDate}
        agents={agents}
        properties={properties}
      />

      {/* Results Info */}
      {!isLeadsLoading && filteredLeads.length > 0 && (
        <div className="text-muted mb-2">
          Showing {(currentPage - 1) * pageSize + 1} -{" "}
          {Math.min(currentPage * pageSize, filteredLeads.length)} of{" "}
          {filteredLeads.length}
        </div>
      )}

      {/* Table / States */}
      {isLeadsLoading ? (
        <div className="leads-skeleton-grid">
          {Array.from({ length: 8 }).map((_, idx) => (
            <div
              key={idx}
              className="skeleton"
              style={{ height: isMobile ? 220 : 120 }}
            />
          ))}
        </div>
      ) : filteredLeads.length === 0 ? (
        <div className="text-center text-muted" style={{ padding: "24px 0" }}>
          No leads found.
        </div>
      ) : (
        <LeadsTable
          isMobile={isMobile}
          leads={paginatedLeads}
          columns={columns}
          handleResend={handleResend}
        />
      )}

      {/* Pagination */}
      {!isLeadsLoading && totalPages > 1 && (
        <div className="d-flex align-center my-3   justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </PageContainer>
  );
}
